import React from 'react';
import { createPortal } from 'react-dom';
import StatsCard from '../../../shared/components/common/StatsCard';
import { DeviceIDBadge } from '../../../shared/components/common/Badges';
import { formatPercentage } from '../../../utils/numberUtils';
import DeviceAuditHelpModal from './DeviceAuditHelpModal';
import { AuditChangeTable } from '../../audit/shared';
import {
  AUDIT_PREVIEW_LIMIT,
  filterAuditEvents,
  getComplianceCardClass,
  getComplianceRateBadgeClass,
  getComplianceRateReason,
  getComplianceRateValue,
  deptHasComplianceImpactingEvents,
  locationHasComplianceImpactingEvents,
  shouldElevateComplianceCard,
} from '../utils/auditComplianceUtils';

const AUDIT_FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'compliance', label: 'Compliance-impacting' },
  { id: 'new', label: 'New' },
  { id: 'modified', label: 'Modified' },
  { id: 'deleted', label: 'Deleted' },
];

const getActionBadgeClass = (action) => {
  if (!action) return 'da-action-badge--unknown';
  if (action.includes('New')) return 'da-action-badge--new';
  if (action.includes('Modified')) return 'da-action-badge--modified';
  if (action.includes('Deleted')) return 'da-action-badge--deleted';
  return 'da-action-badge--unknown';
};

const getIssueBadgeClass = (issue) => {
  const key = String(issue || '').toLowerCase();
  if (key.includes('ppm')) return 'cs-issue-badge--ppm';
  if (key.includes('calibrat')) return 'cs-issue-badge--calibration';
  if (key.includes('regulat')) return 'cs-issue-badge--regulatory';
  return '';
};

const ComplianceRateBadge = ({ rate }) => {
  const value = getComplianceRateValue(rate);
  if (value === null) {
    return (
      <span
        className="cs-rate-badge cs-rate-badge--na"
        title={getComplianceRateReason(rate)}
      >
        --
      </span>
    );
  }
  return (
    <span className={`cs-rate-badge ${getComplianceRateBadgeClass(rate)}`}>
      {formatPercentage(value)}
    </span>
  );
};

const AuditEventDrawer = ({
  event,
  isOpen,
  onClose,
  onNavigateToDevices,
}) => {
  if (!isOpen || !event) return null;

  const isCreate = (event.action || '').toLowerCase().includes('new');
  const isTransfer = (event.action || '').toLowerCase().includes('transfer') || event.event_type === 'transfer';
  const preview = Array.isArray(event.changes_preview) ? event.changes_preview : [];

  const hasMeaningfulValue = (v) => {
    if (v === null || v === undefined) return false;
    const s = String(v).trim();
    if (!s || s === '—' || s.toLowerCase() === 'null') return false;
    return true;
  };

  const filteredDiffs = preview.filter(
    (c) => hasMeaningfulValue(c?.old_value) || hasMeaningfulValue(c?.new_value)
  );
  const totalFields = event.changes_count || filteredDiffs.length;
  const hiddenFieldCount = Math.max(0, totalFields - preview.length);
  const changesSummary = hiddenFieldCount > 0
    ? `Showing ${filteredDiffs.length} of ${totalFields} fields in this action`
    : `${filteredDiffs.length} field${filteredDiffs.length === 1 ? '' : 's'} in this action`;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label="Close drawer backdrop"
        className="da-drawer-backdrop"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClose();
        }}
      />
      <div role="dialog" aria-modal="true" className="da-drawer">
        <div className="da-drawer__header">
          <div style={{ minWidth: 0 }}>
            <div className="da-drawer__title">{event.device_name || 'Unknown Device'}</div>
            <div className="da-drawer__meta">
              <span style={{ fontWeight: 700 }}>{event.action || 'Unknown'}</span>
              <span>•</span>
              <span>{event.user_name || 'Unknown'}</span>
              {event.timestamp && (
                <>
                  <span>•</span>
                  <span>{new Date(event.timestamp).toLocaleString()}</span>
                </>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="da-drawer__close" aria-label="Close">
            ✕
          </button>
        </div>

        <div className="da-drawer__body">
          {event.device_identifier && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 700 }}>
                Device ID
              </div>
              <DeviceIDBadge deviceId={event.device_identifier} size="small" />
            </div>
          )}
          {event.location && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.25rem', fontWeight: 700 }}>
                Location
              </div>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#111827' }}>{event.location}</div>
            </div>
          )}

          {isCreate ? (
            <>
              <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#111827', marginBottom: '0.5rem' }}>
                New device created
              </div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.4 }}>
                This audit entry represents a new device being added. For full details, open the device record.
              </div>
            </>
          ) : isTransfer ? (
            <>
              <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#111827', marginBottom: '0.5rem' }}>
                Device transferred
              </div>
              <div style={{ fontSize: '0.78rem', color: '#111827', fontWeight: 700, marginBottom: '0.25rem' }}>
                {event.from_location || 'N/A'} → {event.to_location || event.location || 'N/A'}
              </div>
              {event.request_reason && (
                <div style={{ fontSize: '0.78rem', color: '#6b7280', lineHeight: 1.4 }}>
                  Reason: {event.request_reason}
                </div>
              )}
            </>
          ) : (
            <div className="da-drawer__changes">
              <div className="da-drawer__changes-heading">What changed</div>
              {filteredDiffs.length > 0 ? (
                <>
                  <p className="da-drawer__changes-summary">{changesSummary}</p>
                  <AuditChangeTable changes={filteredDiffs} />
                  {hiddenFieldCount > 0 && (
                    <p className="da-drawer__changes-more">
                      +{hiddenFieldCount} more field{hiddenFieldCount === 1 ? '' : 's'} changed
                      {onNavigateToDevices && event.device_id
                        ? ' — open device for full audit history'
                        : ''}
                    </p>
                  )}
                </>
              ) : (
                <p className="da-drawer__changes-empty">
                  No field-level details available for this event.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="da-drawer__footer">
          <button type="button" onClick={onClose} className="da-drawer__btn-secondary">
            Close
          </button>
          {onNavigateToDevices && event.device_id && (
            <button
              type="button"
              onClick={() => onNavigateToDevices(event.device_id)}
              className="da-drawer__btn-primary"
            >
              Open device
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default function AuditComplianceSection({
  audit_events,
  compliance_status,
  auditDepartmentFilter,
  setAuditDepartmentFilter,
  auditQuickFilter,
  setAuditQuickFilter,
  selectedAuditEvent,
  setSelectedAuditEvent,
  isAuditDrawerOpen,
  setIsAuditDrawerOpen,
  deptSearchQuery,
  setDeptSearchQuery,
  nonCompliantSearchQuery,
  setNonCompliantSearchQuery,
  showDeviceAuditHelp,
  setShowDeviceAuditHelp,
  onNavigateToDevices,
  variant = 'admin',
}) {
  const isSuperAdmin = variant === 'superadmin';
  const hasAuditData = audit_events && Object.keys(audit_events).length > 0;
  const suspiciousCount = audit_events?.suspicious_events || 0;
  const recentEvents = audit_events?.recent_events || [];
  const filteredEvents = filterAuditEvents(
    recentEvents,
    auditDepartmentFilter,
    auditQuickFilter,
    isSuperAdmin ? 'location' : 'department',
  );
  const filterOptions = isSuperAdmin
    ? [
      ...AUDIT_FILTER_OPTIONS.slice(0, 2),
      { id: 'transfer', label: 'Transfers' },
      ...AUDIT_FILTER_OPTIONS.slice(2),
    ]
    : AUDIT_FILTER_OPTIONS;
  const activityGroups = isSuperAdmin
    ? (audit_events?.top_locations || []).map((loc) => ({
      name: loc.name,
      icon: '📍',
      total_changes: loc.total_changes,
    }))
    : (audit_events?.top_departments || []);
  const complianceGroups = isSuperAdmin
    ? (compliance_status?.by_location || [])
    : (compliance_status?.by_department || []);
  const previewEvents = filteredEvents.slice(0, AUDIT_PREVIEW_LIMIT);
  const overall = compliance_status?.overall_compliance;

  const resetAuditFilters = () => {
    setAuditQuickFilter('all');
    setAuditDepartmentFilter(null);
  };

  const renderComplianceStatCard = (icon, rate, label) => {
    const value = getComplianceRateValue(rate);
    const cardClass = getComplianceCardClass(rate);
    const elevated = shouldElevateComplianceCard(rate);
    return (
      <StatsCard
        icon={icon}
        number={value === null ? 'N/A' : formatPercentage(value)}
        label={label}
        className={`${cardClass}${elevated ? ' alert-elevated' : ''}`.trim()}
        title={value === null ? getComplianceRateReason(rate) : undefined}
      />
    );
  };

  const filteredDepts = complianceGroups.filter((group) => {
    const name = isSuperAdmin ? group.location_name : group.department_name;
    return (name || '').toLowerCase().includes(deptSearchQuery.toLowerCase());
  });

  const filteredNonCompliant = (compliance_status?.non_compliant_devices || []).filter(
    (device) =>
      device.device_name?.toLowerCase().includes(nonCompliantSearchQuery.toLowerCase())
      || device.device_identifier?.toLowerCase().includes(nonCompliantSearchQuery.toLowerCase())
      || device.department?.toLowerCase().includes(nonCompliantSearchQuery.toLowerCase())
      || device.location?.toLowerCase().includes(nonCompliantSearchQuery.toLowerCase())
  );

  return (
    <>
      <div className="list-default-window-note" role="note">
        <span className="list-default-window-note__icon" aria-hidden="true">ℹ️</span>
        <span>
          {isSuperAdmin ? 'Organization-wide' : 'Location-scoped'}. Device audit covers the{' '}
          <strong>last 30 days</strong>; compliance rates reflect current device status.
        </span>
      </div>

      <div className="da-section">
        {hasAuditData ? (
          <>
        <div className="dashboard-subsection-label">Activity summary</div>
        <div className="dashboard-stats-summary" style={{ marginBottom: '0.75rem' }}>
          <StatsCard
            icon="📋"
            number={audit_events.total_devices_audited || 0}
            label="Devices Audited"
            className="total"
            onClick={resetAuditFilters}
            title="Reset filters — last 30 days"
          />
          <StatsCard
            icon="✨"
            number={audit_events.action_breakdown?.new_devices || 0}
            label="New Devices"
            className="active"
            onClick={() => setAuditQuickFilter('new')}
            title="Filter recent changes to new devices"
          />
          <StatsCard
            icon="✏️"
            number={audit_events.action_breakdown?.modifications || 0}
            label="Modified"
            className="maintenance"
            onClick={() => setAuditQuickFilter('modified')}
            title="Filter recent changes to modifications"
          />
          <StatsCard
            icon="🗑️"
            number={audit_events.action_breakdown?.deletions || 0}
            label="Deletions"
            className={`open${(audit_events.action_breakdown?.deletions || 0) > 0 ? ' alert-elevated' : ''}`.trim()}
            onClick={() => setAuditQuickFilter('deleted')}
            title="Filter recent changes to deletions"
          />
          <StatsCard
            icon="⚠️"
            number={suspiciousCount}
            label="Suspicious"
            className={`deactivated${suspiciousCount > 0 ? ' alert-elevated' : ''}`.trim()}
            title="Active devices with more than 5 changes in 24 hours"
          />
        </div>

        {suspiciousCount > 0 && (
          <div className="da-alert-banner">
            {suspiciousCount} suspicious event{suspiciousCount === 1 ? '' : 's'} detected — review
            unusual change patterns in recent changes below.
          </div>
        )}

        {activityGroups.length > 0 && (
          <>
            <div className="dashboard-subsection-label">
              {isSuperAdmin ? 'Top locations by activity' : 'Top departments by activity'}
            </div>
            <div className="da-dept-list">
              {activityGroups.map((group) => (
                <button
                  key={group.name}
                  type="button"
                  className="da-dept-row"
                  onClick={() => setAuditDepartmentFilter(group.name)}
                >
                  <span className="da-dept-row__name">
                    <span>{group.icon}</span>
                    <span>{group.name}</span>
                  </span>
                  <span className="da-dept-row__meta">
                    {(isSuperAdmin
                      ? locationHasComplianceImpactingEvents(recentEvents, group.name)
                      : deptHasComplianceImpactingEvents(recentEvents, group.name)) && (
                      <span className="da-dept-row__badge">Compliance-impacting</span>
                    )}
                    <span className="da-dept-row__total">{group.total_changes} changes</span>
                    <span className="da-dept-row__filter">Filter →</span>
                  </span>
                </button>
              ))}
            </div>
          </>
        )}

        {recentEvents.length > 0 && (
          <>
            <div className="da-recent-header">
              <h4 className="da-recent-header__title">
                Recent changes
                <span className="da-recent-header__count">
                  {filteredEvents.length === 0
                    ? '(none matching filters)'
                    : `(showing ${previewEvents.length} of ${filteredEvents.length})`}
                </span>
              </h4>
            </div>

            <div className="da-filters-row">
              <div className="da-filter-chips">
                {filterOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`da-filter-chip${auditQuickFilter === opt.id ? ' da-filter-chip--active' : ''}`}
                    onClick={() => setAuditQuickFilter(opt.id)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {auditDepartmentFilter && (
                <button
                  type="button"
                  className="da-dept-filter-clear"
                  onClick={() => setAuditDepartmentFilter(null)}
                  title={isSuperAdmin ? 'Clear location filter' : 'Clear department filter'}
                  aria-label={isSuperAdmin ? 'Clear location filter' : 'Clear department filter'}
                >
                  <span style={{ fontSize: '0.85rem', lineHeight: 1 }}>✕</span>
                  <span>{auditDepartmentFilter}</span>
                </button>
              )}
            </div>

            <div className="da-events-table">
              <div className="da-events-table__head">
                <div>Action</div>
                <div>Device</div>
                <div>{isSuperAdmin ? 'Location' : 'Department'}</div>
                <div>User</div>
                <div style={{ textAlign: 'right' }}>Date</div>
              </div>
              {previewEvents.length === 0 ? (
                <div style={{ padding: '1rem 0.75rem', color: '#6b7280', fontSize: '0.8rem' }}>
                  No events match the current filters.
                </div>
              ) : (
                previewEvents.map((auditEvent, idx) => {
                  const previewFields = (auditEvent.changes_preview || [])
                    .map((c) => c?.field_name)
                    .filter(Boolean);
                  const previewText = previewFields.slice(0, 2).join(', ');
                  const remaining = Math.max(0, (auditEvent.changes_count || 0) - previewFields.length);

                  const openDrawer = () => {
                    setSelectedAuditEvent(auditEvent);
                    setIsAuditDrawerOpen(true);
                  };

                  return (
                    <div
                      key={`${auditEvent.device_identifier}-${auditEvent.timestamp}-${idx}`}
                      role="button"
                      tabIndex={0}
                      className="da-events-table__row"
                      onClick={openDrawer}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') openDrawer();
                      }}
                    >
                      <div className={`da-action-badge ${getActionBadgeClass(auditEvent.action)}`}>
                        {auditEvent.action?.includes('New') && '✨'}
                        {auditEvent.action?.includes('Modified') && '✏️'}
                        {auditEvent.action?.includes('Deleted') && '🗑️'}
                        {auditEvent.action || 'Unknown'}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="da-event-device__name">
                          {auditEvent.device_name || 'Unknown Device'}
                        </div>
                        <div className="da-event-device__meta">
                          {auditEvent.device_identifier && (
                            <DeviceIDBadge deviceId={auditEvent.device_identifier} size="small" />
                          )}
                          <span>
                            {auditEvent.changes_count > 0
                              ? `Changed: ${previewText}${remaining > 0 ? ` +${remaining} more` : ''}`
                              : auditEvent.action?.includes('Modified')
                                ? 'Changed: (details unavailable)'
                                : 'Changed: —'}
                          </span>
                        </div>
                      </div>
                      <div className="da-event-dept">
                        <span>{isSuperAdmin ? '📍' : (auditEvent.department_icon || '🏢')}</span>
                        <span className="da-event-dept__name">
                          {isSuperAdmin
                            ? ((auditEvent.action || '').toLowerCase().includes('transfer') && auditEvent.from_location && auditEvent.to_location
                              ? `${auditEvent.from_location} → ${auditEvent.to_location}`
                              : (auditEvent.location || 'N/A'))
                            : (auditEvent.department || 'Unknown')}
                        </span>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="da-event-user__name">{auditEvent.user_name || 'Unknown'}</div>
                        {auditEvent.user_role && (
                          <span className="da-event-user__role">
                            {auditEvent.user_role.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                      <div className="da-events-table__date">
                        {auditEvent.timestamp
                          ? new Date(auditEvent.timestamp).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                          : 'N/A'}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <AuditEventDrawer
              event={selectedAuditEvent}
              isOpen={isAuditDrawerOpen}
              onClose={() => setIsAuditDrawerOpen(false)}
              onNavigateToDevices={onNavigateToDevices}
            />
          </>
        )}
          </>
        ) : (
          <div className="user-mgmt-loading">No device audit activity in the last 30 days.</div>
        )}
      </div>

      {(overall || (complianceGroups.length > 0)
        || (compliance_status?.non_compliant_devices?.length > 0)) && (
        <div className="cs-section">
          {overall && (
            <>
              <div className="dashboard-subsection-label">Compliance rates</div>
              <div className="dashboard-stats-summary" style={{ marginBottom: '0.65rem' }}>
                {renderComplianceStatCard('🔧', overall.ppm_compliance_rate, 'PPM Compliance')}
                {renderComplianceStatCard('📐', overall.calibration_compliance_rate, 'Calibration')}
                {renderComplianceStatCard('📋', overall.regulatory_compliance_rate, 'Regulatory')}
              </div>
            </>
          )}
          <div className="cs-rate-legend" aria-label="Compliance color ranges">
            <span className="cs-rate-legend__item">
              <span className="cs-rate-badge cs-rate-badge--ok">●</span> ≥80%
            </span>
            <span className="cs-rate-legend__item">
              <span className="cs-rate-badge cs-rate-badge--warn">●</span> 60–79%
            </span>
            <span className="cs-rate-legend__item">
              <span className="cs-rate-badge cs-rate-badge--bad">●</span> &lt;60%
            </span>
            <span className="cs-rate-legend__item">
              <span className="cs-rate-badge cs-rate-badge--na">--</span> N/A
            </span>
          </div>

          {complianceGroups.length > 0 && (
            <div className="cs-block">
              <div className="dashboard-subsection-label">
                {isSuperAdmin
                  ? `Compliance by location (${complianceGroups.length})`
                  : `Compliance by department (${complianceGroups.length})`}
              </div>
              <input
                type="text"
                className="cs-search-input"
                placeholder={isSuperAdmin ? 'Search locations...' : 'Search departments...'}
                value={deptSearchQuery}
                onChange={(e) => setDeptSearchQuery(e.target.value)}
                aria-label={isSuperAdmin ? 'Search locations' : 'Search departments'}
              />
              <div className="cs-table-wrap">
                <table className="cs-table">
                  <thead>
                    <tr>
                      <th>{isSuperAdmin ? 'Location' : 'Department'}</th>
                      <th className="cs-table__col--center">PPM</th>
                      <th className="cs-table__col--center">Calibration</th>
                      <th className="cs-table__col--center">Regulatory</th>
                      <th className="cs-table__col--center">Devices</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDepts.map((group) => (
                      <tr key={isSuperAdmin ? group.location_name : group.department_name}>
                        <td>
                          <div className="cs-table__dept-cell">
                            <span style={{ fontSize: '1rem' }}>{isSuperAdmin ? '📍' : group.department_icon}</span>
                            <span className="cs-table__dept-name">
                              {isSuperAdmin ? group.location_name : group.department_name}
                            </span>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <ComplianceRateBadge rate={group.ppm_compliance} />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <ComplianceRateBadge rate={group.calibration_compliance} />
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <ComplianceRateBadge rate={group.regulatory_compliance} />
                        </td>
                        <td style={{ textAlign: 'center', color: '#666', fontWeight: 500 }}>
                          {group.total_devices}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredDepts.length === 0 && deptSearchQuery && (
                <div className="cs-empty-search">
                  {isSuperAdmin ? 'No locations match your search' : 'No departments match your search'}
                </div>
              )}
            </div>
          )}

          {compliance_status?.non_compliant_devices && compliance_status.non_compliant_devices.length > 0 && (
            <div className="cs-block">
              <div className="dashboard-subsection-label">
                Non-compliant devices ({compliance_status.non_compliant_devices.length})
              </div>
              <input
                type="text"
                className="cs-search-input"
                placeholder={isSuperAdmin
                  ? 'Search by device name, ID, or location...'
                  : 'Search by device name, ID, or department...'}
                value={nonCompliantSearchQuery}
                onChange={(e) => setNonCompliantSearchQuery(e.target.value)}
                aria-label="Search non-compliant devices"
              />
              <div className="cs-table-wrap">
                <table className="cs-table">
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>ID</th>
                      <th>{isSuperAdmin ? 'Location' : 'Department'}</th>
                      <th>Compliance Issues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredNonCompliant.map((device, idx) => {
                      const clickable = onNavigateToDevices && device.device_id;
                      return (
                        <tr
                          key={`${device.device_id || device.device_identifier}-${idx}`}
                          className={clickable ? 'cs-table__row--clickable' : undefined}
                          onClick={() => {
                            if (clickable) onNavigateToDevices(device.device_id);
                          }}
                        >
                          <td>
                            <div style={{ fontWeight: 500, color: '#333' }}>
                              {device.device_name || 'Unknown Device'}
                            </div>
                          </td>
                          <td>
                            {device.device_identifier ? (
                              <DeviceIDBadge deviceId={device.device_identifier} size="small" />
                            ) : (
                              <span style={{ color: '#999', fontSize: '0.75rem' }}>N/A</span>
                            )}
                          </td>
                          <td>
                            <div className="cs-table__dept-cell">
                              <span>{isSuperAdmin ? '📍' : (device.department_icon || '🏢')}</span>
                              <span>{isSuperAdmin ? (device.location || 'Unassigned') : (device.department || 'Unassigned')}</span>
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                              {device.issues && device.issues.length > 0 ? (
                                device.issues.map((issue, i) => (
                                  <span key={i} className={`cs-issue-badge ${getIssueBadgeClass(issue)}`}>{issue}</span>
                                ))
                              ) : (
                                <span style={{ color: '#999', fontSize: '0.75rem' }}>None detected</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredNonCompliant.length === 0 && nonCompliantSearchQuery && (
                <div className="cs-empty-search">No devices match your search</div>
              )}
            </div>
          )}
        </div>
      )}

      <DeviceAuditHelpModal
        isOpen={showDeviceAuditHelp}
        onClose={() => setShowDeviceAuditHelp(false)}
        variant={variant}
      />
    </>
  );
}
